import { html } from 'htm/preact'

export function PerformanceMetrics({ data }) {
  if (!data) return null

  const formatDuration = (seconds) => {
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
    if (seconds < 60) return `${seconds.toFixed(1)}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}h ${minutes}m`
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const getPerformanceColor = (value, thresholds) => {
    if (value >= thresholds.danger) return 'text-red-600 dark:text-red-400'
    if (value >= thresholds.warning) return 'text-yellow-600 dark:text-yellow-400'
    return 'text-green-600 dark:text-green-400'
  }

  const getPerformanceIcon = (value, thresholds) => {
    if (value >= thresholds.danger) {
      return html`
        <svg class="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      `
    }
    if (value >= thresholds.warning) {
      return html`
        <svg class="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      `
    }
    return html`
      <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>
    `
  }

  const cpuThresholds = { warning: 70, danger: 90 }
  const memoryThresholds = { warning: 70, danger: 90 }
  const diskThresholds = { warning: 80, danger: 95 }

  return html`
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <svg class="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
          </svg>
          Performance Metrics
        </h2>
      </div>
      
      <div class="p-6 space-y-6">
        <!-- System Resources -->
        ${data.system && html`
          <div>
            <h3 class="text-md font-medium text-gray-900 dark:text-white mb-4">System Resources</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- CPU Usage -->
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    ${getPerformanceIcon(data.system.cpu_usage, cpuThresholds)}
                    <span class="text-sm font-medium text-gray-900 dark:text-white">CPU Usage</span>
                  </div>
                  <span class="${getPerformanceColor(data.system.cpu_usage, cpuThresholds)} font-semibold">
                    ${data.system.cpu_usage?.toFixed(1) || 0}%
                  </span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    class="bg-blue-500 h-2 rounded-full" 
                    style="width: ${Math.min(data.system.cpu_usage || 0, 100)}%"
                  ></div>
                </div>
              </div>

              <!-- Memory Usage -->
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    ${getPerformanceIcon(data.system.memory_usage, memoryThresholds)}
                    <span class="text-sm font-medium text-gray-900 dark:text-white">Memory</span>
                  </div>
                  <span class="${getPerformanceColor(data.system.memory_usage, memoryThresholds)} font-semibold">
                    ${data.system.memory_usage?.toFixed(1) || 0}%
                  </span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    class="bg-green-500 h-2 rounded-full" 
                    style="width: ${Math.min(data.system.memory_usage || 0, 100)}%"
                  ></div>
                </div>
                ${data.system.memory_total && html`
                  <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    ${formatBytes(data.system.memory_used || 0)} / ${formatBytes(data.system.memory_total)}
                  </p>
                `}
              </div>

              <!-- Disk Usage -->
              <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    ${getPerformanceIcon(data.system.disk_usage, diskThresholds)}
                    <span class="text-sm font-medium text-gray-900 dark:text-white">Disk</span>
                  </div>
                  <span class="${getPerformanceColor(data.system.disk_usage, diskThresholds)} font-semibold">
                    ${data.system.disk_usage?.toFixed(1) || 0}%
                  </span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div 
                    class="bg-purple-500 h-2 rounded-full" 
                    style="width: ${Math.min(data.system.disk_usage || 0, 100)}%"
                  ></div>
                </div>
                ${data.system.disk_total && html`
                  <p class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    ${formatBytes(data.system.disk_used || 0)} / ${formatBytes(data.system.disk_total)}
                  </p>
                `}
              </div>
            </div>
          </div>
        `}

        <!-- Application Performance -->
        ${data.application && html`
          <div>
            <h3 class="text-md font-medium text-gray-900 dark:text-white mb-4">Application Performance</h3>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div class="text-center">
                <p class="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  ${data.application.avg_scrape_time ? formatDuration(data.application.avg_scrape_time) : 'N/A'}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">Avg Scrape Time</p>
              </div>
              
              <div class="text-center">
                <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                  ${data.application.success_rate ? `${data.application.success_rate.toFixed(1)}%` : 'N/A'}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">Success Rate</p>
              </div>
              
              <div class="text-center">
                <p class="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ${data.application.active_connections || 0}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">Active Connections</p>
              </div>
              
              <div class="text-center">
                <p class="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  ${data.application.queue_size || 0}
                </p>
                <p class="text-sm text-gray-600 dark:text-gray-400">Queue Size</p>
              </div>
            </div>
          </div>
        `}

        <!-- Database Performance -->
        ${data.database && html`
          <div>
            <h3 class="text-md font-medium text-gray-900 dark:text-white mb-4">Database Performance</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-3">
                <div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Connection Pool</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    ${data.database.pool_active || 0} / ${data.database.pool_size || 0}
                  </span>
                </div>
                <div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Avg Query Time</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    ${data.database.avg_query_time ? formatDuration(data.database.avg_query_time) : 'N/A'}
                  </span>
                </div>
                <div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Slow Queries</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    ${data.database.slow_queries || 0}
                  </span>
                </div>
              </div>
              
              <div class="space-y-3">
                <div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Total Articles</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    ${data.database.total_articles?.toLocaleString() || 0}
                  </span>
                </div>
                <div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Database Size</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    ${data.database.db_size ? formatBytes(data.database.db_size) : 'N/A'}
                  </span>
                </div>
                <div class="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-600">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Cache Hit Rate</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    ${data.database.cache_hit_rate ? `${data.database.cache_hit_rate.toFixed(1)}%` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        `}

        <!-- Recent Performance Trends -->
        ${data.trends && data.trends.length > 0 && html`
          <div>
            <h3 class="text-md font-medium text-gray-900 dark:text-white mb-4">Recent Trends</h3>
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div class="space-y-2">
                ${data.trends.slice(0, 5).map((trend, index) => html`
                  <div key=${index} class="flex items-center justify-between text-sm">
                    <span class="text-gray-600 dark:text-gray-400">
                      ${new Date(trend.timestamp).toLocaleTimeString()}
                    </span>
                    <div class="flex gap-4 text-xs">
                      <span>CPU: ${trend.cpu?.toFixed(1) || 0}%</span>
                      <span>MEM: ${trend.memory?.toFixed(1) || 0}%</span>
                      <span>Scrapes: ${trend.scrape_count || 0}</span>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>
        `}

        ${!data.system && !data.application && !data.database && html`
          <div class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Performance Data</h3>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Performance metrics are not available at this time.
            </p>
          </div>
        `}
      </div>
    </div>
  `
}